import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { gameAPI } from '../../services/gameAPI';

const TUTORIAL_THEMES = [
  {
    id: 'noir',
    name: 'Film Noir',
    era: '1940s',
    description: 'Classic black-and-white mystery at a recording studio. Learn investigation basics with a noir detective aesthetic.',
    gradient: 'from-slate-900 to-slate-800',
    accentColor: '#DC143C',
    icon: '🎭'
  },
  {
    id: 'vice',
    name: 'Miami Vice',
    era: '1980s',
    description: 'Neon-soaked art world murder in Miami. Master investigation techniques with pastel suits and palm trees.',
    gradient: 'from-purple-900 via-pink-800 to-cyan-900',
    accentColor: '#FF1493',
    icon: '🌴'
  },
  {
    id: '90s_nyc',
    name: '1990s New York City',
    era: '1990s',
    description: 'Gritty urban detective story in the Bronx. Hip-hop culture meets street art investigation.',
    gradient: 'from-orange-900 to-blue-900',
    accentColor: '#FF6B35',
    icon: '🏙️'
  }
];

export const TutorialThemeModal = ({ isOpen, onClose, onThemeSelect }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [hoveredTheme, setHoveredTheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableThemes, setAvailableThemes] = useState(TUTORIAL_THEMES);

  const fetchAvailableThemes = useCallback(async () => {
    try {
      const response = await gameAPI.getTutorialThemes();

      // Defensively check if response has valid themes array
      if (!response?.themes || !Array.isArray(response.themes) || response.themes.length === 0) {
        setAvailableThemes(TUTORIAL_THEMES);
        return;
      }

      // Match backend themes with our display data
      const backendThemeIds = response.themes.map(t => t.id);
      const filteredThemes = TUTORIAL_THEMES.filter(t => backendThemeIds.includes(t.id));
      setAvailableThemes(filteredThemes);
    } catch (error) {
      console.error('Failed to fetch tutorial themes:', error);
      // Fallback to hardcoded themes if API fails
      setAvailableThemes(TUTORIAL_THEMES);
    }
  }, []);

  // Fetch available themes from backend when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableThemes();
    }
  }, [isOpen, fetchAvailableThemes]);

  const handleStartTutorial = async () => {
    if (!selectedTheme) return;

    setLoading(true);
    try {
      // Call the callback with selected theme
      await onThemeSelect(selectedTheme);
      onClose();
    } catch (error) {
      console.error('Failed to start tutorial:', error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedTheme(null);
      setHoveredTheme(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-stone-900 border-2 border-amber-500 text-stone-100 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-amber-400 mb-2">
            Choose Your Tutorial Theme
          </DialogTitle>
          <DialogDescription className="text-stone-300 text-base">
            Each tutorial teaches the same investigation mechanics, but with a unique atmosphere and setting.
            Pick the era that speaks to you!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-6">
          {availableThemes.map((theme) => (
            <div
              key={theme.id}
              role="button"
              tabIndex={theme.comingSoon ? -1 : 0}
              aria-disabled={loading || theme.comingSoon}
              onClick={() => !loading && !theme.comingSoon && setSelectedTheme(theme.id)}
              onKeyDown={(e) => {
                if (!loading && !theme.comingSoon && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setSelectedTheme(theme.id);
                }
              }}
              onMouseEnter={() => !loading && !theme.comingSoon && setHoveredTheme(theme.id)}
              onMouseLeave={() => setHoveredTheme(null)}
              onFocus={() => !loading && !theme.comingSoon && setHoveredTheme(theme.id)}
              onBlur={() => setHoveredTheme(null)}
              className={`relative p-6 rounded-lg border-2 transition-all duration-300 ${
                theme.comingSoon
                  ? 'border-stone-700 bg-stone-800/20 opacity-60 cursor-not-allowed'
                  : selectedTheme === theme.id
                  ? 'border-amber-500 bg-amber-500/10 scale-[1.02] cursor-pointer'
                  : hoveredTheme === theme.id
                  ? 'border-amber-400/50 bg-stone-800/50 scale-[1.01] cursor-pointer'
                  : 'border-stone-700 bg-stone-800/30 cursor-pointer'
              }`}
              style={{
                boxShadow: selectedTheme === theme.id
                  ? `0 0 20px ${theme.accentColor}40`
                  : 'none'
              }}
            >
              <div className="flex items-start gap-4">
                {/* Theme Icon */}
                <div
                  className={`text-5xl flex-shrink-0 transition-transform duration-300 ${
                    hoveredTheme === theme.id ? 'scale-110' : 'scale-100'
                  }`}
                >
                  {theme.icon}
                </div>

                {/* Theme Details */}
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-2">
                    <h3
                      className="text-2xl font-bold text-stone-100"
                      style={{
                        color: selectedTheme === theme.id ? theme.accentColor : undefined
                      }}
                    >
                      {theme.name}
                    </h3>
                    <span className="text-sm font-semibold text-stone-400 tracking-wide">
                      {theme.era}
                    </span>
                  </div>
                  <p className="text-stone-300 leading-relaxed">
                    {theme.description}
                  </p>
                </div>

                {/* Selection Indicator */}
                {selectedTheme === theme.id && (
                  <div
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: theme.accentColor }}
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Gradient Preview Bar */}
              <div
                className={`h-2 rounded-full mt-4 bg-gradient-to-r ${theme.gradient}`}
              />

              {/* Coming Soon Banner */}
              {theme.comingSoon && (
                <div className="absolute top-0 right-0 bg-amber-600 text-white text-xs font-bold px-4 py-2 rounded-bl-lg rounded-tr-lg shadow-lg">
                  COMING SOON
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-8">
          <Button
            onClick={handleClose}
            variant="outline"
            className="flex-1 bg-stone-700 hover:bg-stone-600 text-stone-100 border-stone-600"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartTutorial}
            disabled={!selectedTheme || loading}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting Tutorial...
              </>
            ) : (
              'Start Tutorial'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
